import React from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Paper,
} from '@mui/material';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RepeatIcon from '@mui/icons-material/Repeat';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const C = {
  navy:      '#273F77',
  orange:    '#E79A2B',
  bg:        '#f5f6fa',
  cardBg:    '#ffffff',
  border:    '#e8eaf0',
  textMuted: '#8492a6',
  green:     '#2e7d32',
  red:       '#c62828',
};

const StatCard = ({ icon, label, value, highlight, success }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: '16px',
      border: `1.5px solid ${success ? C.green : highlight ? C.navy : C.border}`,
      p: 2.5, textAlign: 'center', flex: 1,
      backgroundColor: success ? 'rgba(46,125,50,0.04)'
        : highlight ? 'rgba(39,63,119,0.04)' : C.cardBg,
    }}
  >
    <Box display="flex" justifyContent="center" mb={1}>
      {React.cloneElement(icon, {
        sx: { fontSize: 28, color: success ? C.green : highlight ? C.navy : C.textMuted },
      })}
    </Box>
    <Typography variant="h4" fontWeight={800} lineHeight={1}
      color={success ? C.green : highlight ? C.navy : 'text.primary'}>
      {value}
    </Typography>
    <Typography variant="caption" color={C.textMuted} fontWeight={600}
      mt={0.5} display="block" textTransform="uppercase" letterSpacing={0.5}>
      {label}
    </Typography>
  </Paper>
);

const QuizIntro = ({ quiz, attemptsInfo, onStart, loading }) => {
  const alreadyPassed  = attemptsInfo?.passed === true;
  const noAttemptsLeft = !alreadyPassed && attemptsInfo?.attempts_remaining === 0;
  const isBlocked      = alreadyPassed || noAttemptsLeft;

  return (
    <Box maxWidth={580} mx="auto">

      {/* Title block */}
      <Box textAlign="center" mb={4}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '18px', mx: 'auto', mb: 2.5,
          backgroundColor: alreadyPassed ? 'rgba(46,125,50,0.1)' : 'rgba(39,63,119,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {alreadyPassed
            ? <CheckCircleIcon sx={{ fontSize: 38, color: C.green }} />
            : <QuizOutlinedIcon sx={{ fontSize: 36, color: C.navy }} />
          }
        </Box>
        <Typography variant="h4" fontWeight={800}
          color={alreadyPassed ? C.green : C.navy} mb={1}>
          {quiz.title}
        </Typography>
        <Typography variant="body1" color={C.textMuted}>
          {alreadyPassed
            ? 'You have already passed this quiz. Great work!'
            : 'Review the quiz details below before you begin.'}
        </Typography>
      </Box>

      {/* Stat cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <StatCard icon={<QuizOutlinedIcon />}        label="Questions"  value={quiz.total_questions} highlight />
        <StatCard icon={<AccessTimeIcon />}           label="Time Limit" value={`${quiz.estimated_time_req}m`} />
        <StatCard icon={<CheckCircleOutlineIcon />}   label="Pass Score" value={`${quiz.passing_score}%`} />
        <StatCard
          icon={<RepeatIcon />}
          label="Attempts Left"
          value={loading ? '...' : attemptsInfo ? attemptsInfo.attempts_remaining : '-'}
          highlight={!alreadyPassed && attemptsInfo && attemptsInfo.attempts_remaining > 0}
          success={alreadyPassed}
        />
      </Box>



      {/* Passed alert */}
      {alreadyPassed && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 3, borderRadius: '12px' }}
        >
          You passed this quiz with a score of <strong>{attemptsInfo?.score}/{quiz.total_questions}</strong>.
          No further attempts are needed.
        </Alert>
      )}

      {/* No attempts alert */}
      {noAttemptsLeft && (
        <Alert
          severity="error"
          icon={<LockOutlinedIcon />}
          sx={{ mb: 3, borderRadius: '12px' }}
        >
          You have used all {attemptsInfo.attempts_allowed} attempt{attemptsInfo.attempts_allowed !== 1 ? 's' : ''} for this quiz.
        </Alert>
      )}

      <Button
        fullWidth variant="contained" size="large"
        onClick={onStart}
        disabled={loading || isBlocked}
        startIcon={
          loading         ? <CircularProgress size={18} color="inherit" />
          : alreadyPassed  ? <CheckCircleIcon />
          : noAttemptsLeft ? <LockOutlinedIcon />
          : <QuizOutlinedIcon />
        }
        sx={{
          backgroundColor: alreadyPassed ? 'rgba(46,125,50,0.12)'
            : isBlocked ? '#e0e0e0' : C.orange,
          color: alreadyPassed ? C.green : isBlocked ? '#9e9e9e' : '#fff',
          borderRadius: '12px', textTransform: 'none',
          fontWeight: 700, py: 1.6, fontSize: '1rem',
          boxShadow: isBlocked ? 'none' : '0 4px 14px rgba(231,154,43,0.4)',
          border: alreadyPassed ? `1.5px solid ${C.green}` : 'none',
          '&:hover': {
            backgroundColor: alreadyPassed ? 'rgba(46,125,50,0.12)'
              : isBlocked ? '#e0e0e0' : '#d4891f',
          },
          '&.Mui-disabled': {
            backgroundColor: alreadyPassed ? 'rgba(46,125,50,0.12)' : '#e0e0e0',
            color: alreadyPassed ? C.green : '#9e9e9e',
          },
        }}
      >
        {loading          ? 'Loading...'
          : alreadyPassed ? 'Already Passed'
          : noAttemptsLeft ? 'No Attempts Remaining'
          : 'Start Quiz'}
      </Button>
    </Box>
  );
};

export default QuizIntro;